import React, { useEffect, useState, useCallback } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Table,
  FormGroup,
  Label,
  Input,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  Alert
} from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { withTranslation } from "react-i18next";
import * as XLSX from 'xlsx';

const apiUrl = process.env.REACT_APP_API_URL;

const Reconciliation = (props) => {
  const { t } = props;
  const [reconciliationData, setReconciliationData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState(getLastTuesday());
  const [endDate, setEndDate] = useState(getNextMonday(startDate));
  const [sellers, setSellers] = useState([]);
  const [selectedSeller, setSelectedSeller] = useState("");
  const [modal, setModal] = useState(false);
  const [currentRecord, setCurrentRecord] = useState({});
  const [addonItems, setAddonItems] = useState([]);
  const [totalSummary, setTotalSummary] = useState({
    frontCommission: 0,
    backCommission: 0,
    addonsTotal: 0,
    clawBack: 0,
    total: 0
  });

  // Manager commission states
  const [managerCommissionData, setManagerCommissionData] = useState([]);
  const [loadingManagers, setLoadingManagers] = useState(false);
  const [managerModal, setManagerModal] = useState(false);
  const [currentManagerRecord, setCurrentManagerRecord] = useState({});
  const [managerSuccessMessage, setManagerSuccessMessage] = useState("");
  const [managerErrorMessage, setManagerErrorMessage] = useState("");
  const [managerTotalCommission, setManagerTotalCommission] = useState(0);

  // Function to get last Tuesday
  function getLastTuesday() {
    const today = new Date();
    const day = today.getDay();
    const diff = (day >= 2) ? day - 2 : 7 - (2 - day);
    const lastTuesday = new Date(today);
    lastTuesday.setDate(today.getDate() - diff);
    return lastTuesday;
  }

  // Function to get next Monday from a given date
  function getNextMonday(date) {
    const next = new Date(date);
    next.setDate(date.getDate() + 6);
    return next;
  }

  // Format date to YYYY-MM-DD for API calls and inputs
  const formatDateForApi = (date) => {
    return date.toISOString().split('T')[0];
  };

  // Export Reconciliation Data to XLSX
  const exportReconciliationToXLSX = () => {
    if (!reconciliationData.length) {
      alert(t("No data to export"));
      return;
    }

    // Prepare data for export
    const exportData = reconciliationData.map(record => ({
      'ID': record.id,
      'Date Range': `${new Date(record.startDate).toLocaleDateString()} - ${new Date(record.endDate).toLocaleDateString()}`,
      'Seller Name': record.seller,
      'Seller Email': record.sellerEmail,
      'Customer': record.customer,
      'Size': record.size,
      'Card ID': record.cardId,
      'Rate': typeof record.rate === 'number' ? record.rate.toFixed(2) : '0.00',
      'Share %': typeof record.share === 'number' ? record.share.toFixed(1) + '%' : '100.0%',
      'Type': record.type?.charAt(0).toUpperCase() + record.type?.slice(1) || '',
      'Front Commission': typeof record.frontCommission === 'number' ? record.frontCommission.toFixed(2) : '0.00',
      'Back Commission': typeof record.backCommission === 'number' ? record.backCommission.toFixed(2) : '0.00',
      'Addons': record.formattedAddons || '-',
      'Claw Back': typeof record.clawBack === 'number' ? record.clawBack.toFixed(2) : '0.00',
      'Total': typeof parseFloat(record.total) === 'number' && !isNaN(parseFloat(record.total)) ? parseFloat(record.total).toFixed(2) : '0.00',
      'Last Modified': record.lastModified ? new Date(record.lastModified).toLocaleDateString() : '',
      'Modified By': record.modifiedBy || ''
    }));

    // Create workbook
    const wb = XLSX.utils.book_new();
    
    // Add reconciliation data sheet
    const ws = XLSX.utils.json_to_sheet(exportData);
    
    // Set column widths
    const columnWidths = [
      { wch: 8 },   // ID
      { wch: 20 },  // Date Range
      { wch: 15 },  // Seller Name
      { wch: 25 },  // Seller Email
      { wch: 20 },  // Customer
      { wch: 8 },   // Size
      { wch: 12 },  // Card ID
      { wch: 10 },  // Rate
      { wch: 10 },  // Share %
      { wch: 12 },  // Type
      { wch: 15 },  // Front Commission
      { wch: 15 },  // Back Commission
      { wch: 30 },  // Addons
      { wch: 12 },  // Claw Back
      { wch: 12 },  // Total
      { wch: 15 },  // Last Modified
      { wch: 15 }   // Modified By
    ];
    ws['!cols'] = columnWidths;

    XLSX.utils.book_append_sheet(wb, ws, "Reconciliation Data");

    // Add summary sheet
    const summaryData = [
      { 'Metric': 'Front Commission', 'Amount': (parseFloat(totalSummary.frontCommission) || 0).toFixed(2) },
      { 'Metric': 'Back Commission', 'Amount': (parseFloat(totalSummary.backCommission) || 0).toFixed(2) },
      { 'Metric': 'Addons Total', 'Amount': (parseFloat(totalSummary.addonsTotal) || 0).toFixed(2) },
      { 'Metric': 'Claw Back', 'Amount': (parseFloat(totalSummary.clawBack) || 0).toFixed(2) },
      { 'Metric': 'Manager Commission', 'Amount': (parseFloat(managerTotalCommission) || 0).toFixed(2) },
      { 'Metric': 'Seller Total', 'Amount': (parseFloat(totalSummary.total) || 0).toFixed(2) },
      { 'Metric': 'Net Total', 'Amount': ((parseFloat(totalSummary.total) || 0) - (parseFloat(managerTotalCommission) || 0)).toFixed(2) }
    ];

    const summaryWs = XLSX.utils.json_to_sheet(summaryData);
    summaryWs['!cols'] = [{ wch: 20 }, { wch: 15 }];
    XLSX.utils.book_append_sheet(wb, summaryWs, "Summary");

    // Generate filename
    const dateRange = `${formatDateForApi(startDate)}_to_${formatDateForApi(endDate)}`;
    const sellerSuffix = selectedSeller ? `_${selectedSeller.replace('@', '_at_')}` : '_all_sellers';
    const filename = `Reconciliation_${dateRange}${sellerSuffix}.xlsx`;

    // Save file
    XLSX.writeFile(wb, filename);
  };

  // Export Manager Commission Data to XLSX
  const exportManagerCommissionToXLSX = () => {
    if (!managerCommissionData.length) {
      alert(t("No manager commission data to export"));
      return;
    }

    // Prepare data for export
    const exportData = managerCommissionData.map(record => ({
      'ID': record.id,
      'Date Range': `${new Date(record.startDate).toLocaleDateString()} - ${new Date(record.endDate).toLocaleDateString()}`,
      'Manager': record.managerEmail,
      'Seller': record.sellerName,
      'Customer': record.customer,
      'Card ID': record.cardId,
      'Sale Amount': (parseFloat(record.saleAmount) || 0).toFixed(2),
      'Commission Type': record.commissionType?.charAt(0).toUpperCase() + record.commissionType?.slice(1) || '',
      'Percentage Applied': record.percentageApplied + '%',
      'Commission Amount': (parseFloat(record.commissionAmount) || 0).toFixed(2)
    }));

    // Create workbook
    const wb = XLSX.utils.book_new();
    
    // Add manager commission data sheet
    const ws = XLSX.utils.json_to_sheet(exportData);
    
    // Set column widths
    const columnWidths = [
      { wch: 8 },   // ID
      { wch: 20 },  // Date Range
      { wch: 25 },  // Manager
      { wch: 20 },  // Seller
      { wch: 20 },  // Customer
      { wch: 12 },  // Card ID
      { wch: 15 },  // Sale Amount
      { wch: 18 },  // Commission Type
      { wch: 18 },  // Percentage Applied
      { wch: 18 }   // Commission Amount
    ];
    ws['!cols'] = columnWidths;

    XLSX.utils.book_append_sheet(wb, ws, "Manager Commissions");

    // Add manager summary sheet
    const managerSummaryData = [
      { 'Metric': 'Total Manager Commission', 'Amount': (parseFloat(managerTotalCommission) || 0).toFixed(2) },
      { 'Metric': 'Number of Records', 'Amount': managerCommissionData.length.toString() }
    ];

    const managerSummaryWs = XLSX.utils.json_to_sheet(managerSummaryData);
    managerSummaryWs['!cols'] = [{ wch: 25 }, { wch: 15 }];
    XLSX.utils.book_append_sheet(wb, managerSummaryWs, "Manager Summary");

    // Generate filename
    const dateRange = `${formatDateForApi(startDate)}_to_${formatDateForApi(endDate)}`;
    const sellerSuffix = selectedSeller ? `_${selectedSeller.replace('@', '_at_')}` : '_all_sellers';
    const filename = `Manager_Commissions_${dateRange}${sellerSuffix}.xlsx`;

    // Save file
    XLSX.writeFile(wb, filename);
  };

  // Export Combined Data to XLSX
  const exportCombinedToXLSX = () => {
    if (!reconciliationData.length && !managerCommissionData.length) {
      alert(t("No data to export"));
      return;
    }

    // Create workbook
    const wb = XLSX.utils.book_new();

    // Add reconciliation data if available
    if (reconciliationData.length > 0) {
      const reconciliationExportData = reconciliationData.map(record => ({
        'ID': record.id,
        'Date Range': `${new Date(record.startDate).toLocaleDateString()} - ${new Date(record.endDate).toLocaleDateString()}`,
        'Seller Name': record.seller,
        'Seller Email': record.sellerEmail,
        'Customer': record.customer,
        'Size': record.size,
        'Card ID': record.cardId,
        'Rate': typeof record.rate === 'number' ? record.rate.toFixed(2) : '0.00',
        'Share %': typeof record.share === 'number' ? record.share.toFixed(1) + '%' : '100.0%',
        'Type': record.type?.charAt(0).toUpperCase() + record.type?.slice(1) || '',
        'Front Commission': typeof record.frontCommission === 'number' ? record.frontCommission.toFixed(2) : '0.00',
        'Back Commission': typeof record.backCommission === 'number' ? record.backCommission.toFixed(2) : '0.00',
        'Addons': record.formattedAddons || '-',
        'Claw Back': typeof record.clawBack === 'number' ? record.clawBack.toFixed(2) : '0.00',
        'Total': typeof parseFloat(record.total) === 'number' && !isNaN(parseFloat(record.total)) ? parseFloat(record.total).toFixed(2) : '0.00',
        'Last Modified': record.lastModified ? new Date(record.lastModified).toLocaleDateString() : '',
        'Modified By': record.modifiedBy || ''
      }));

      const reconciliationWs = XLSX.utils.json_to_sheet(reconciliationExportData);
      reconciliationWs['!cols'] = [
        { wch: 8 }, { wch: 20 }, { wch: 15 }, { wch: 25 }, { wch: 20 }, { wch: 8 }, { wch: 12 },
        { wch: 10 }, { wch: 10 }, { wch: 12 }, { wch: 15 }, { wch: 15 }, { wch: 30 }, { wch: 12 },
        { wch: 12 }, { wch: 15 }, { wch: 15 }
      ];
      XLSX.utils.book_append_sheet(wb, reconciliationWs, "Reconciliation Data");
    }

    // Add manager commission data if available
    if (managerCommissionData.length > 0) {
      const managerExportData = managerCommissionData.map(record => ({
        'ID': record.id,
        'Date Range': `${new Date(record.startDate).toLocaleDateString()} - ${new Date(record.endDate).toLocaleDateString()}`,
        'Manager': record.managerEmail,
        'Seller': record.sellerName,
        'Customer': record.customer,
        'Card ID': record.cardId,
        'Sale Amount': (parseFloat(record.saleAmount) || 0).toFixed(2),
        'Commission Type': record.commissionType?.charAt(0).toUpperCase() + record.commissionType?.slice(1) || '',
        'Percentage Applied': record.percentageApplied + '%',
        'Commission Amount': (parseFloat(record.commissionAmount) || 0).toFixed(2)
      }));

      const managerWs = XLSX.utils.json_to_sheet(managerExportData);
      managerWs['!cols'] = [
        { wch: 8 }, { wch: 20 }, { wch: 25 }, { wch: 20 }, { wch: 20 }, { wch: 12 },
        { wch: 15 }, { wch: 18 }, { wch: 18 }, { wch: 18 }
      ];
      XLSX.utils.book_append_sheet(wb, managerWs, "Manager Commissions");
    }

    // Add combined summary sheet
    const combinedSummaryData = [
      { 'Category': 'Seller Commissions', 'Metric': 'Front Commission', 'Amount': (parseFloat(totalSummary.frontCommission) || 0).toFixed(2) },
      { 'Category': 'Seller Commissions', 'Metric': 'Back Commission', 'Amount': (parseFloat(totalSummary.backCommission) || 0).toFixed(2) },
      { 'Category': 'Seller Commissions', 'Metric': 'Addons Total', 'Amount': (parseFloat(totalSummary.addonsTotal) || 0).toFixed(2) },
      { 'Category': 'Seller Commissions', 'Metric': 'Claw Back', 'Amount': (parseFloat(totalSummary.clawBack) || 0).toFixed(2) },
      { 'Category': 'Seller Commissions', 'Metric': 'Seller Total', 'Amount': (parseFloat(totalSummary.total) || 0).toFixed(2) },
      { 'Category': 'Manager Commissions', 'Metric': 'Manager Total', 'Amount': (parseFloat(managerTotalCommission) || 0).toFixed(2) },
      { 'Category': 'Net Totals', 'Metric': 'Net Total', 'Amount': ((parseFloat(totalSummary.total) || 0) - (parseFloat(managerTotalCommission) || 0)).toFixed(2) }
    ];

    const combinedSummaryWs = XLSX.utils.json_to_sheet(combinedSummaryData);
    combinedSummaryWs['!cols'] = [{ wch: 20 }, { wch: 20 }, { wch: 15 }];
    XLSX.utils.book_append_sheet(wb, combinedSummaryWs, "Combined Summary");

    // Generate filename
    const dateRange = `${formatDateForApi(startDate)}_to_${formatDateForApi(endDate)}`;
    const sellerSuffix = selectedSeller ? `_${selectedSeller.replace('@', '_at_')}` : '_all_sellers';
    const filename = `Complete_Reconciliation_${dateRange}${sellerSuffix}.xlsx`;

    // Save file
    XLSX.writeFile(wb, filename);
  };

  // Function to format addons for display
  const formatAddonsForDisplay = (addonsData) => {
    if (!addonsData) return "";
    
    try {
      if (typeof addonsData === 'string' && !addonsData.trim().startsWith('[')) {
        return addonsData;
      }
      
      const addonsArray = typeof addonsData === 'string' 
        ? JSON.parse(addonsData) 
        : addonsData;
      
      if (Array.isArray(addonsArray)) {
        if (addonsArray.length === 0) return "";
        return addonsArray.map(addon => 
          `${addon.name}: $${parseFloat(addon.value).toFixed(2)}`
        ).join(", ");
      }
      
      if (typeof addonsArray === 'object' && addonsArray !== null) {
        return `${addonsArray.name}: $${parseFloat(addonsArray.value).toFixed(2)}`;
      }
      
      return String(addonsData);
    } catch (e) {
      console.error("Error formatting addons:", e);
      return String(addonsData);
    }
  };

  // Calculate row total with share percentage applied
  const calculateRowTotal = (record) => {
    const frontCommission = parseFloat(record.frontCommission) || 0;
    const backCommission = parseFloat(record.backCommission) || 0;
    const addonsTotal = parseFloat(record.addons_total) || 0;
    const clawBack = parseFloat(record.clawBack) || 0;
    const share = parseFloat(record.share) || 100;
    
    const baseTotal = frontCommission + backCommission + addonsTotal - clawBack;
    const totalWithShare = baseTotal * (share / 100);
    
    return totalWithShare.toFixed(2);
  };

  // Fetch sellers
  const fetchSellers = useCallback(async () => {
    try {
      const authUser = JSON.parse(localStorage.getItem("authUser"));
      const token = authUser?.token;
      if (!token) throw new Error("Authentication token not found");

      const response = await fetch(`${apiUrl}/api/data/sellers`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) throw new Error("Failed to fetch sellers");

      const data = await response.json();
      setSellers(data);
    } catch (error) {
      console.error("Error fetching sellers:", error);
      setSellers([
        { name: "John Doe", seller_email: "john@example.com" },
        { name: "Jane Smith", seller_email: "jane@example.com" }
      ]);
    }
  }, []);

  // Fetch reconciliation data
  const fetchReconciliationData = useCallback(async () => {
    setLoading(true);
    try {
      const authUser = JSON.parse(localStorage.getItem("authUser"));
      const token = authUser?.token;
      if (!token) throw new Error("Authentication token not found");

      const formattedStartDate = formatDateForApi(startDate);
      const formattedEndDate = formatDateForApi(endDate);
      
      let url = `${apiUrl}/api/data/reconciliation?start_date=${formattedStartDate}&end_date=${formattedEndDate}`;
      
      if (selectedSeller) {
        url += `&seller_email=${encodeURIComponent(selectedSeller)}`;
      }

      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) throw new Error("Failed to fetch reconciliation data");

      const data = await response.json();
      
      const commissionData = data.commissions || [];
      
      const enhancedData = commissionData.map(record => ({
        ...record,
        frontCommission: parseFloat(record.frontCommission) || 0,
        backCommission: parseFloat(record.backCommission) || 0,
        addons_total: parseFloat(record.addons_total) || 0,
        clawBack: parseFloat(record.clawBack) || 0,
        rate: parseFloat(record.rate) || 0,
        share: parseFloat(record.share) || 100,
        total: calculateRowTotal(record),
        rawAddons: record.addons,
        formattedAddons: formatAddonsForDisplay(record.addons)
      }));
      
      setReconciliationData(enhancedData);
      calculateSummaryTotals(enhancedData);
    } catch (error) {
      console.error("Error fetching reconciliation data:", error);
      setReconciliationData([]);
      setTotalSummary({
        frontCommission: 0,
        backCommission: 0,
        addonsTotal: 0,
        clawBack: 0,
        total: 0
      });
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, selectedSeller]);

  // Fetch manager commission data
  const fetchManagerCommissionData = useCallback(async () => {
    setLoadingManagers(true);
    try {
      const authUser = JSON.parse(localStorage.getItem("authUser"));
      const token = authUser?.token;
      if (!token) throw new Error("Authentication token not found");

      const formattedStartDate = formatDateForApi(startDate);
      const formattedEndDate = formatDateForApi(endDate);
      
      let url = `${apiUrl}/api/data/manager-commissions?start_date=${formattedStartDate}&end_date=${formattedEndDate}`;
      
      if (selectedSeller) {
        url += `&seller_email=${encodeURIComponent(selectedSeller)}`;
      }

      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) throw new Error("Failed to fetch manager commission data");

      const data = await response.json();
      
      const enhancedData = (data.managerCommissions || []).map(record => ({
        ...record,
        commissionAmount: parseFloat(record.commissionAmount) || 0,
        percentageApplied: parseFloat(record.percentageApplied) || 0,
      }));
      
      setManagerCommissionData(enhancedData);
      
      const total = enhancedData.reduce((sum, item) => sum + parseFloat(item.commissionAmount || 0), 0);
      setManagerTotalCommission(total);
    } catch (error) {
      console.error("Error fetching manager commission data:", error);
      setManagerCommissionData([]);
      setManagerTotalCommission(0);
    } finally {
      setLoadingManagers(false);
    }
  }, [startDate, endDate, selectedSeller]);

  // Calculate summary totals with share percentages applied
  const calculateSummaryTotals = (data) => {
    const summary = data.reduce((acc, record) => {
      const share = parseFloat(record.share) || 100;
      const shareMultiplier = share / 100;
      
      acc.frontCommission += (parseFloat(record.frontCommission) || 0) * shareMultiplier;
      acc.backCommission += (parseFloat(record.backCommission) || 0) * shareMultiplier;
      acc.addonsTotal += (parseFloat(record.addons_total) || 0) * shareMultiplier;
      acc.clawBack += (parseFloat(record.clawBack) || 0) * shareMultiplier;
      return acc;
    }, {
      frontCommission: 0,
      backCommission: 0,
      addonsTotal: 0,
      clawBack: 0
    });

    summary.total = summary.frontCommission + summary.backCommission + summary.addonsTotal - summary.clawBack;
    setTotalSummary(summary);
  };

  // Handle date range change
  const handleDateRangeChange = (e, isStart) => {
    const date = new Date(e.target.value);
    if (isStart) {
      setStartDate(date);
      setEndDate(getNextMonday(date));
    } else {
      setEndDate(date);
    }
  };

  // Handle seller change
  const handleSellerChange = (e) => {
    setSelectedSeller(e.target.value);
  };

  // Parse addons string to array of addon items
  const parseAddons = (addonsString) => {
    if (!addonsString) return [];
    
    try {
      if (typeof addonsString === 'object' && !Array.isArray(addonsString)) {
        return [addonsString];
      }
      
      if (typeof addonsString === 'object' && Array.isArray(addonsString)) {
        return addonsString;
      }
      
      const parsed = JSON.parse(addonsString);
      if (Array.isArray(parsed)) return parsed;
      if (typeof parsed === 'object') return [parsed];
      
      return parseAddonsString(addonsString);
    } catch (e) {
      console.error("Error parsing addons JSON:", e);
      return parseAddonsString(String(addonsString));
    }
  };
  
  // Parse addons from string format
  const parseAddonsString = (addonsString) => {
    if (!addonsString || typeof addonsString !== 'string') return [];
    
    try {
      if (addonsString.trim() === '[]') return [];
      
      return addonsString.split(',').map(item => {
        const [name, value] = item.split(':');
        return {
          name: name?.trim() || '',
          value: parseFloat(value) || 0
        };
      }).filter(item => item.name);
    } catch (e) {
      console.error("Error parsing addons string format:", e);
      return [];
    }
  };

  // Calculate addons total from addon items
  const calculateAddonsTotal = (items) => {
    if (!Array.isArray(items)) return 0;
    return items.reduce((sum, item) => {
      const value = typeof item.value === 'string' 
        ? parseFloat(item.value) 
        : (typeof item.value === 'number' ? item.value : 0);
      return sum + (value || 0);
    }, 0);
  };

  // Handle record edit
  const handleEditRecord = (record) => {
    const parsedAddons = parseAddons(record.rawAddons);
    
    setCurrentRecord({
      ...record,
      rate: parseFloat(record.rate) || 0,
      addons_total: parseFloat(record.addons_total) || 0,
      clawBack: parseFloat(record.clawBack) || 0,
      share: parseFloat(record.share) || 100
    });
    
    setAddonItems(parsedAddons);
    setModal(true);
  };

  // Handle manager commission edit
  const handleEditManagerCommission = (record) => {
    setCurrentManagerRecord({
      ...record,
      percentageApplied: parseFloat(record.percentageApplied) || 0,
      originalAmount: parseFloat(record.commissionAmount) || 0,
      newPercentage: parseFloat(record.percentageApplied) || 0,
      newAmount: parseFloat(record.commissionAmount) || 0,
      saleAmount: parseFloat(record.saleAmount) || 0
    });
    setManagerSuccessMessage("");
    setManagerErrorMessage("");
    setManagerModal(true);
  };
  
  // Handle manager percentage change
  const handleManagerPercentageChange = (e) => {
    const newPercentage = parseFloat(e.target.value) || 0;
    const saleAmount = currentManagerRecord.saleAmount || 0;
    const newAmount = (saleAmount * newPercentage) / 100;
    
    setCurrentManagerRecord({
      ...currentManagerRecord,
      newPercentage,
      newAmount
    });
  };

  // Handle addon item change
  const handleAddonChange = (index, field, value) => {
    const updatedAddons = [...addonItems];
    updatedAddons[index] = {
      ...updatedAddons[index],
      [field]: field === 'value' ? (parseFloat(value) || 0) : value
    };
    
    setAddonItems(updatedAddons);
    
    const total = calculateAddonsTotal(updatedAddons);
    setCurrentRecord(prev => ({
      ...prev,
      addons_total: total
    }));
  };

  // Add new addon item
  const handleAddAddon = () => {
    setAddonItems([...addonItems, { name: '', value: 0 }]);
  };

  // Remove addon item
  const handleRemoveAddon = (index) => {
    const updatedAddons = addonItems.filter((_, i) => i !== index);
    setAddonItems(updatedAddons);
    
    const total = calculateAddonsTotal(updatedAddons);
    setCurrentRecord(prev => ({
      ...prev,
      addons_total: total
    }));
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentRecord({ ...currentRecord, [name]: value });
  };

  // Handle seller form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const authUser = JSON.parse(localStorage.getItem("authUser"));
      const token = authUser?.token;
      if (!token) throw new Error("Authentication token not found");

      const validAddonItems = addonItems.map(item => ({
        name: String(item.name || ''),
        value: parseFloat(item.value) || 0
      }));

      const addonsString = JSON.stringify(validAddonItems);
      
      const response = await fetch(`${apiUrl}/api/data/reconciliation/${currentRecord.id}`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify({
          rate: parseFloat(currentRecord.rate) || 0,
          addons: addonsString,
          addons_total: parseFloat(currentRecord.addons_total) || 0,
          claw_back: parseFloat(currentRecord.clawBack) || 0,
          share: parseFloat(currentRecord.share) || 100
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || "Failed to update record");
      }

      fetchReconciliationData();
      fetchManagerCommissionData();
      setModal(false);
    } catch (error) {
      console.error("Error updating record:", error);
      alert(`Error updating record: ${error.message}`);
    }
  };

  // Handle manager form submission
  const handleManagerSubmit = async (e) => {
    e.preventDefault();
    try {
      const authUser = JSON.parse(localStorage.getItem("authUser"));
      const token = authUser?.token;
      if (!token) throw new Error("Authentication token not found");

      const response = await fetch(`${apiUrl}/api/data/manager-commissions/${currentManagerRecord.id}`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify({
          percentage_applied: currentManagerRecord.newPercentage,
          commission_amount: currentManagerRecord.newAmount,
          notes: `Percentage manually adjusted from ${currentManagerRecord.percentageApplied}% to ${currentManagerRecord.newPercentage}%`
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || "Failed to update manager commission");
      }

      setManagerSuccessMessage("Manager commission updated successfully!");
      
      setManagerCommissionData(prevData => 
        prevData.map(record => 
          record.id === currentManagerRecord.id ? 
          { 
            ...record, 
            percentageApplied: currentManagerRecord.newPercentage,
            commissionAmount: currentManagerRecord.newAmount,
          } : 
          record
        )
      );
      
      setManagerTotalCommission(prevTotal => 
        prevTotal - currentManagerRecord.originalAmount + currentManagerRecord.newAmount
      );
      
      setTimeout(() => {
        setManagerModal(false);
      }, 1500);
    } catch (error) {
      console.error("Error updating manager commission:", error);
      setManagerErrorMessage(`Failed to update: ${error.message}`);
    }
  };

  // Handle seller modal toggle
  const toggle = () => {
    setModal(!modal);
    if (modal) {
      setAddonItems([]);
    }
  };

  // Handle manager modal toggle
  const toggleManagerModal = () => {
    setManagerModal(!managerModal);
    if (!managerModal) {
      setManagerSuccessMessage("");
      setManagerErrorMessage("");
    }
  };

  // Load data on component mount
  useEffect(() => {
    fetchSellers();
  }, [fetchSellers]);

  // Load reconciliation data and manager commission data when filters change
  useEffect(() => {
    fetchReconciliationData();
    fetchManagerCommissionData();
  }, [fetchReconciliationData, fetchManagerCommissionData]);

  // Meta title
  document.title = "Reconciliation | Sunwave";

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title={t("Reconciliation")} breadcrumbItem={t("Reconciliation")} />

          {/* Date and Seller Filter */}
          <Row className="mb-4">
            <Col lg="12">
              <Card>
                <CardBody>
                  <Row className="mb-3">
                    <Col md="4">
                      <FormGroup>
                        <Label>{t("Start Date (Tuesday)")}</Label>
                        <Input
                          type="date"
                          className="form-control"
                          value={formatDateForApi(startDate)}
                          onChange={(e) => handleDateRangeChange(e, true)}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="4">
                      <FormGroup>
                        <Label>{t("End Date (Monday)")}</Label>
                        <Input
                          type="date"
                          className="form-control"
                          value={formatDateForApi(endDate)}
                          onChange={(e) => handleDateRangeChange(e, false)}
                          min={formatDateForApi(startDate)}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="4">
                      <FormGroup>
                        <Label>{t("Seller")}</Label>
                        <Input
                          type="select"
                          value={selectedSeller}
                          onChange={handleSellerChange}
                        >
                          <option value="">{t("All Sellers")}</option>
                          {sellers.map((seller, index) => (
                            <option key={index} value={seller.seller_email}>
                              {seller.seller_email}
                            </option>
                          ))}
                        </Input>
                      </FormGroup>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Summary Section */}
          <Row className="mb-4">
            <Col lg="12">
              <Card>
                <CardBody>
                  <h4 className="card-title mb-4">{t("Summary")}</h4>
                  <Row>
                    <Col md="2">
                      <div className="mb-3">
                        <h5>{t("Front Commission")}</h5>
                        <h4>${(parseFloat(totalSummary.frontCommission) || 0).toFixed(2)}</h4>
                      </div>
                    </Col>
                    <Col md="2">
                      <div className="mb-3">
                        <h5>{t("Back Commission")}</h5>
                        <h4>${(parseFloat(totalSummary.backCommission) || 0).toFixed(2)}</h4>
                      </div>
                    </Col>
                    <Col md="2">
                      <div className="mb-3">
                        <h5>{t("Addons Total")}</h5>
                        <h4>${(parseFloat(totalSummary.addonsTotal) || 0).toFixed(2)}</h4>
                      </div>
                    </Col>
                    <Col md="2">
                      <div className="mb-3">
                        <h5>{t("Claw Back")} (subtracted)</h5>
                        <h4>-${(parseFloat(totalSummary.clawBack) || 0).toFixed(2)}</h4>
                      </div>
                    </Col>
                    <Col md="2">
                      <div className="mb-3">
                        <h5>{t("Manager Commission")}</h5>
                        <h4>${(parseFloat(managerTotalCommission) || 0).toFixed(2)}</h4>
                      </div>
                    </Col>
                    <Col md="2">
                      <div className="mb-3">
                        <h5>{t("Net Total")}</h5>
                        <h4 className="text-success">
                          ${((parseFloat(totalSummary.total) || 0) - (parseFloat(managerTotalCommission) || 0)).toFixed(2)}
                        </h4>
                      </div>
                    </Col>
                  </Row>
                  <Row>
                    <Col md="12">
                      <Alert color="success" className="mb-0">
                        <h5 className="mb-0">
                          {t("Seller Total")}: ${(parseFloat(totalSummary.total) || 0).toFixed(2)} | 
                          {" " + t("Manager Total")}: ${(parseFloat(managerTotalCommission) || 0).toFixed(2)} | 
                          {" " + t("Net Total")}: ${((parseFloat(totalSummary.total) || 0) - (parseFloat(managerTotalCommission) || 0)).toFixed(2)}
                        </h5>
                      </Alert>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Reconciliation Data Table */}
          <Row>
            <Col lg="12">
              <Card>
                <CardBody>
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <h4 className="card-title">{t("Reconciliation Data")}</h4>
                    <div className="d-flex gap-2">
                      <Button
                        color="success"
                        size="sm"
                        onClick={exportReconciliationToXLSX}
                        disabled={!reconciliationData.length}
                      >
                        <i className="bx bx-download me-1"></i>
                        {t("Export Excel")}
                      </Button>
                    </div>
                  </div>
                  <div className="table-responsive">
                    <Table className="table table-centered table-nowrap mb-0">
                      <thead className="thead-light">
                        <tr>
                          <th>{t("ID")}</th>
                          <th>{t("Date Range")}</th>
                          <th>{t("Seller Name")}</th>
                          <th>{t("Seller Email")}</th>
                          <th>{t("Customer")}</th>
                          <th>{t("Size")}</th>
                          <th>{t("Card ID")}</th>
                          <th>{t("Rate")}</th>
                          <th>{t("Share %")}</th>
                          <th>{t("Type")}</th>
                          <th>{t("Front Commission")}</th>
                          <th>{t("Back Commission")}</th>
                          <th>{t("Addons")}</th>
                          <th>{t("Claw Back")}</th>
                          <th>{t("Total")}</th>
                          <th>{t("Last Modified")}</th>
                          <th>{t("Actions")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {loading ? (
                          <tr>
                            <td colSpan="17" className="text-center">
                              {t("Loading...")}
                            </td>
                          </tr>
                        ) : reconciliationData.length === 0 ? (
                          <tr>
                            <td colSpan="17" className="text-center">
                              {t("No records found")}
                            </td>
                          </tr>
                        ) : (
                          reconciliationData.map((record, index) => (
                            <tr key={index}>
                              <td>{record.id}</td>
                              <td>
                                {new Date(record.startDate).toLocaleDateString()} - {new Date(record.endDate).toLocaleDateString()}
                              </td>
                              <td>{record.seller}</td>
                              <td>{record.sellerEmail}</td>
                              <td>{record.customer}</td>
                              <td>{record.size}</td>
                              <td>{record.cardId}</td>
                              <td>${typeof record.rate === 'number' ? record.rate.toFixed(2) : '0.00'}</td>
                              <td>{typeof record.share === 'number' ? record.share.toFixed(1) : '100.0'}%</td>
                              <td>{t(record.type?.charAt(0).toUpperCase() + record.type?.slice(1) || '')}</td>
                              <td>${typeof record.frontCommission === 'number' ? record.frontCommission.toFixed(2) : '0.00'}</td>
                              <td>${typeof record.backCommission === 'number' ? record.backCommission.toFixed(2) : '0.00'}</td>
                              <td className="addons-cell">
                                {record.formattedAddons ? (
                                  <div className="addons-list">
                                    {record.formattedAddons.split(', ').map((addon, addonIdx) => (
                                      <div key={addonIdx} className="addon-item">
                                        {addon}
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  "-"
                                )}
                              </td>
                              <td>${typeof record.clawBack === 'number' ? record.clawBack.toFixed(2) : '0.00'}</td>
                              <td className="font-weight-bold">${typeof parseFloat(record.total) === 'number' && !isNaN(parseFloat(record.total)) ? parseFloat(record.total).toFixed(2) : '0.00'}</td>
                              <td>
                                {record.lastModified ? new Date(record.lastModified).toLocaleDateString() : ''} {record.modifiedBy || ''}
                              </td>
                              <td>
                                <Button
                                  color="primary"
                                  size="sm"
                                  onClick={() => handleEditRecord(record)}
                                >
                                  {t("Edit")}
                                </Button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </Table>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Manager Commission Table */}
          <Row className="mt-4">
            <Col lg="12">
              <Card>
                <CardBody>
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <div className="d-flex justify-content-between align-items-center w-100">
                      <h4 className="card-title">{t("Manager Commission Data")}</h4>
                      <div className="d-flex align-items-center gap-3">
                        <h5 className="text-success mb-0">
                          {t("Total")}: ${(parseFloat(managerTotalCommission) || 0).toFixed(2)}
                        </h5>
                        <div className="d-flex gap-2">
                          <Button
                            color="success"
                            size="sm"
                            onClick={exportManagerCommissionToXLSX}
                            disabled={!managerCommissionData.length}
                          >
                            <i className="bx bx-download me-1"></i>
                            {t("Export Excel")}
                          </Button>
                          <Button
                            color="info"
                            size="sm"
                            onClick={exportCombinedToXLSX}
                            disabled={!reconciliationData.length && !managerCommissionData.length}
                          >
                            <i className="bx bx-download me-1"></i>
                            {t("Export All Data")}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="table-responsive">
                    <Table className="table table-centered table-nowrap mb-0">
                      <thead className="thead-light">
                        <tr>
                          <th>{t("ID")}</th>
                          <th>{t("Date Range")}</th>
                          <th>{t("Manager")}</th>
                          <th>{t("Seller")}</th>
                          <th>{t("Customer")}</th>
                          <th>{t("Card ID")}</th>
                          <th>{t("Sale Amount")}</th>
                          <th>{t("Commission Type")}</th>
                          <th>{t("Percentage Applied")}</th>
                          <th>{t("Commission Amount")}</th>
                          <th>{t("Actions")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {loadingManagers ? (
                          <tr>
                            <td colSpan="11" className="text-center">
                              {t("Loading...")}
                            </td>
                          </tr>
                        ) : managerCommissionData.length === 0 ? (
                          <tr>
                            <td colSpan="11" className="text-center">
                              {t("No manager commission records found")}
                            </td>
                          </tr>
                        ) : (
                          managerCommissionData.map((record, index) => (
                            <tr key={index}>
                              <td>{record.id}</td>
                              <td>
                                {new Date(record.startDate).toLocaleDateString()} - {new Date(record.endDate).toLocaleDateString()}
                              </td>
                              <td>{record.managerEmail}</td>
                              <td>{record.sellerName}</td>
                              <td>{record.customer}</td>
                              <td>{record.cardId}</td>
                              <td>${(parseFloat(record.saleAmount) || 0).toFixed(2)}</td>
                              <td>{t(record.commissionType?.charAt(0).toUpperCase() + record.commissionType?.slice(1) || '')}</td>
                              <td>{record.percentageApplied}%</td>
                              <td>${(parseFloat(record.commissionAmount) || 0).toFixed(2)}</td>
                              <td>
                                <Button
                                  color="primary"
                                  size="sm"
                                  onClick={() => handleEditManagerCommission(record)}
                                >
                                  {t("Edit")}
                                </Button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </Table>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Edit Seller Modal */}
          <Modal isOpen={modal} toggle={toggle} size="lg">
            <ModalHeader toggle={toggle}>{t("Edit Reconciliation Record")}</ModalHeader>
            <Form onSubmit={handleSubmit}>
              <ModalBody>
                {/* Non-editable Fields */}
                <Row>
                  <Col md="6">
                    <FormGroup>
                      <Label>{t("Seller")}</Label>
                      <Input 
                        type="text" 
                        name="sellerEmail" 
                        value={currentRecord.seller || currentRecord.sellerEmail || ""} 
                        disabled 
                      />
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <Label>{t("Customer")}</Label>
                      <Input 
                        type="text" 
                        name="customer" 
                        value={currentRecord.customer || ""} 
                        disabled 
                      />
                    </FormGroup>
                  </Col>
                </Row>
                <Row>
                  <Col md="4">
                    <FormGroup>
                      <Label>{t("Size")}</Label>
                      <Input 
                        type="number" 
                        name="size" 
                        value={currentRecord.size || ""} 
                        disabled 
                      />
                    </FormGroup>
                  </Col>
                  <Col md="4">
                    <FormGroup>
                      <Label>{t("Card ID")}</Label>
                      <Input 
                        type="text" 
                        name="cardId" 
                        value={currentRecord.cardId || ""} 
                        disabled 
                      />
                    </FormGroup>
                  </Col>
                  <Col md="4">
                    <FormGroup>
                      <Label>{t("Rate")}</Label>
                      <Input 
                        type="number" 
                        step="0.01" 
                        name="rate" 
                        value={currentRecord.rate || ""} 
                        onChange={handleInputChange} 
                      />
                    </FormGroup>
                  </Col>
                </Row>
                <Row>
                  <Col md="4">
                    <FormGroup>
                      <Label>{t("Share %")}</Label>
                      <Input 
                        type="number" 
                        step="0.1" 
                        min="0"
                        max="100"
                        name="share" 
                        value={currentRecord.share || ""} 
                        onChange={handleInputChange} 
                      />
                      <small className="form-text text-muted">
                        {t("Percentage share of the total commission (0-100%)")}
                      </small>
                    </FormGroup>
                  </Col>
                  <Col md="4">
                    <FormGroup>
                      <Label>{t("Type")}</Label>
                      <Input 
                        type="text" 
                        name="type" 
                        value={currentRecord.type?.charAt(0).toUpperCase() + currentRecord.type?.slice(1) || ""} 
                        disabled 
                      />
                    </FormGroup>
                  </Col>
                  <Col md="4">
                    <FormGroup>
                      <Label>{t("Phase")}</Label>
                      <Input 
                        type="text" 
                        name="phase" 
                        value={currentRecord.phase || ""} 
                        disabled 
                      />
                    </FormGroup>
                  </Col>
                </Row>
                <Row>
                  <Col md="6">
                    <FormGroup>
                      <Label>{t("Front Commission")}</Label>
                      <Input 
                        type="number" 
                        step="0.01" 
                        name="frontCommission" 
                        value={currentRecord.frontCommission || ""} 
                        disabled 
                      />
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <Label>{t("Back Commission")}</Label>
                      <Input 
                        type="number" 
                        step="0.01" 
                        name="backCommission" 
                        value={currentRecord.backCommission || ""} 
                        disabled 
                      />
                    </FormGroup>
                  </Col>
                </Row>
                
                {/* Addons Table */}
                <FormGroup>
                  <Label>{t("Addons")}</Label>
                  <div className="table-responsive mb-2">
                    <Table className="table table-centered table-bordered mb-0">
                      <thead className="thead-light">
                        <tr>
                          <th>{t("Addon Name")}</th>
                          <th>{t("Value ($)")}</th>
                          <th>{t("Actions")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {addonItems.length === 0 ? (
                          <tr>
                            <td colSpan="3" className="text-center">
                              {t("No addons added")}
                            </td>
                          </tr>
                        ) : (
                          addonItems.map((addon, index) => (
                            <tr key={index}>
                              <td>
                                <Input
                                  type="text"
                                  value={addon.name || ""}
                                  onChange={(e) => handleAddonChange(index, 'name', e.target.value)}
                                  placeholder={t("Enter addon name")}
                                />
                              </td>
                              <td>
                                <Input
                                  type="number"
                                  step="0.01"
                                  value={addon.value || ""}
                                  onChange={(e) => handleAddonChange(index, 'value', e.target.value)}
                                  placeholder="0.00"
                                />
                              </td>
                              <td style={{ width: "100px" }}>
                                <Button
                                  color="danger"
                                  size="sm"
                                  onClick={() => handleRemoveAddon(index)}
                                >
                                  <i className="bx bx-trash"></i>
                                </Button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </Table>
                  </div>
                  <Button
                    color="success"
                    size="sm"
                    onClick={handleAddAddon}
                    type="button"
                  >
                    <i className="bx bx-plus"></i> {t("Add Addon")}
                  </Button>
                </FormGroup>
                
                <Row>
                  <Col md="6">
                    <FormGroup>
                      <Label>{t("Addons Total")}</Label>
                      <Input 
                        type="number" 
                        step="0.01" 
                        name="addons_total" 
                        value={currentRecord.addons_total || ""}
                        readOnly
                        disabled
                      />
                      <small className="form-text text-muted">
                        {t("This is automatically calculated from addon values")}
                      </small>
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <Label>{t("Claw Back")}</Label>
                      <Input 
                        type="number" 
                        step="0.01" 
                        name="clawBack" 
                        value={currentRecord.clawBack || ""} 
                        onChange={handleInputChange} 
                      />
                    </FormGroup>
                  </Col>
                </Row>
                <FormGroup>
                  <Label>{t("Notes")}</Label>
                  <Input 
                    type="textarea" 
                    name="notes" 
                    value={currentRecord.notes || ""} 
                    disabled 
                  />
                </FormGroup>
              </ModalBody>
              <ModalFooter>
                <Button color="secondary" onClick={toggle}>
                  {t("Cancel")}
                </Button>{" "}
                <Button color="primary" type="submit">
                  {t("Save")}
                </Button>
              </ModalFooter>
            </Form>
          </Modal>

           {/* Manager Commission Edit Modal */}
           <Modal isOpen={managerModal} toggle={toggleManagerModal}>
           <ModalHeader toggle={toggleManagerModal}>{t("Edit Manager Commission")}</ModalHeader>
           <Form onSubmit={handleManagerSubmit}>
             <ModalBody>
               {managerSuccessMessage && (
                 <Alert color="success" className="mb-3">
                   {managerSuccessMessage}
                 </Alert>
               )}
               {managerErrorMessage && (
                 <Alert color="danger" className="mb-3">
                   {managerErrorMessage}
                 </Alert>
               )}
               <Row>
                 <Col md="6">
                   <FormGroup>
                     <Label>{t("Manager")}</Label>
                     <Input 
                       type="text" 
                       value={currentManagerRecord.managerEmail || ""} 
                       disabled 
                     />
                   </FormGroup>
                 </Col>
                 <Col md="6">
                   <FormGroup>
                     <Label>{t("Seller")}</Label>
                     <Input 
                       type="text" 
                       value={currentManagerRecord.sellerName || currentManagerRecord.sellerEmail || ""} 
                       disabled 
                     />
                   </FormGroup>
                 </Col>
               </Row>
               <Row>
                 <Col md="6">
                   <FormGroup>
                     <Label>{t("Customer")}</Label>
                     <Input 
                       type="text" 
                       value={currentManagerRecord.customer || ""} 
                       disabled 
                     />
                   </FormGroup>
                 </Col>
                 <Col md="6">
                   <FormGroup>
                     <Label>{t("Commission Type")}</Label>
                     <Input 
                       type="text" 
                       value={currentManagerRecord.commissionType?.charAt(0).toUpperCase() + currentManagerRecord.commissionType?.slice(1) || ""} 
                       disabled 
                     />
                   </FormGroup>
                 </Col>
               </Row>
               <Row>
                 <Col md="6">
                   <FormGroup>
                     <Label>{t("Sale Amount")}</Label>
                     <Input 
                       type="text" 
                       value={`${(currentManagerRecord.saleAmount || 0).toFixed(2)}`} 
                       disabled 
                     />
                   </FormGroup>
                 </Col>
                 <Col md="6">
                   <FormGroup>
                     <Label>{t("Current Commission Amount")}</Label>
                     <Input 
                       type="text" 
                       value={`${(currentManagerRecord.originalAmount || 0).toFixed(2)}`} 
                       disabled 
                     />
                   </FormGroup>
                 </Col>
               </Row>
               <Row>
                 <Col md="6">
                   <FormGroup>
                     <Label>{t("Current Percentage")}</Label>
                     <Input 
                       type="text" 
                       value={`${currentManagerRecord.percentageApplied || 0}%`} 
                       disabled 
                     />
                   </FormGroup>
                 </Col>
                 <Col md="6">
                   <FormGroup>
                     <Label>{t("New Percentage")}</Label>
                     <Input 
                       type="number" 
                       step="0.01" 
                       min="0" 
                       max="100" 
                       value={currentManagerRecord.newPercentage || ""} 
                       onChange={handleManagerPercentageChange} 
                     />
                   </FormGroup>
                 </Col>
               </Row>
               <Row>
                 <Col md="12">
                   <FormGroup>
                     <Label>{t("New Commission Amount")}</Label>
                     <Input 
                       type="text" 
                       value={`${(currentManagerRecord.newAmount || 0).toFixed(2)}`} 
                       disabled 
                     />
                     <small className="text-muted">
                       {t("This is calculated automatically based on the sale amount and new percentage")}
                     </small>
                   </FormGroup>
                 </Col>
               </Row>
             </ModalBody>
             <ModalFooter>
               <Button color="secondary" onClick={toggleManagerModal}>
                 {t("Cancel")}
               </Button>{" "}
               <Button color="primary" type="submit">
                 {t("Save")}
               </Button>
             </ModalFooter>
           </Form>
         </Modal>
       </Container>
     </div>
     
     {/* CSS styles for better addon visualization */}
     <style>
       {`
         .addons-cell {
           max-width: 250px;
         }
         
         .addons-list {
           display: flex;
           flex-direction: column;
         }
         
         .addon-item {
           margin-bottom: 3px;
           padding: 2px 0;
         }
         
         .addon-item:not(:last-child) {
           border-bottom: 1px dashed #e9ecef;
         }
       `}
     </style>
   </React.Fragment>
 );
};

export default withTranslation()(Reconciliation);